import Link from "next/link";
import type { Listing } from "@/lib/construction/types";
import { cn } from "@/lib/cn";
import { companyHref, getRegion } from "@/lib/construction/places";
import { getTrade } from "@/lib/construction/trades";
import { websiteHost } from "@/lib/construction/normalise";

type CompanyRowProps = {
  listing: Listing;
  showRegion?: boolean;
  headingLevel?: "h2" | "h3";
  onHover?: (region: string | null) => void;
};

/** One company in a result list. Only stored facts are shown; each database value carries its inline-edit key. */
export function CompanyRow({ listing: l, showRegion = true, headingLevel = "h3", onHover }: CompanyRowProps) {
  const Heading = headingLevel;
  const region = getRegion(l.region);
  const cms = (column: string) => `companies:${l.rowId}:${column}`;
  const host = websiteHost(l.website);
  return (
    <li
      onMouseEnter={onHover ? () => onHover(l.region) : undefined}
      onMouseLeave={onHover ? () => onHover(null) : undefined}
      onFocus={onHover ? () => onHover(l.region) : undefined}
      onBlur={onHover ? () => onHover(null) : undefined}
    >
      <article className="group relative flex h-full flex-col gap-3 rounded-2xl border border-ink/[0.08] bg-white p-5 transition-colors hover:border-ink/25">
        <div className="flex items-start justify-between gap-3">
          <Heading className="font-display text-base font-bold leading-snug tracking-tight text-ink md:text-lg">
            <Link
              href={companyHref(l.region, l.townSlug, l.slug)}
              className="after:absolute after:inset-0 after:rounded-2xl focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-ink"
            >
              <span data-mdlfy-cms={cms("name")}>{l.name}</span>
            </Link>
          </Heading>
          {l.ranking ? (
            <span className="shrink-0 rounded-full border border-accent bg-accent/15 px-2.5 py-0.5 text-[11px] font-semibold text-ink">
              Size data
            </span>
          ) : null}
        </div>
        <p className="text-sm text-ink-muted">
          <span data-mdlfy-cms={cms("town")}>{l.town}</span>
          {showRegion && region ? <span>, {region.name}</span> : null}
          {l.address || l.postcode ? (
            <span className="block text-xs">
              {l.source === "ch" ? <span className="font-medium text-ink/70">Registered office: </span> : null}
              {l.address ? <span data-mdlfy-cms={cms("address")}>{l.address}</span> : null}
              {l.address && l.postcode ? ", " : null}
              {l.postcode ? <span data-mdlfy-cms={cms("postcode")}>{l.postcode}</span> : null}
            </span>
          ) : null}
          {host ? (
            <span className="block text-xs">
              <span className="font-medium text-ink/70">Website: </span>
              {host}
            </span>
          ) : null}
        </p>
        {l.trades.length ? (
          <ul aria-label="Trades" className="mt-auto flex flex-wrap gap-1.5">
            {l.trades.slice(0, 3).map((slug) => (
              <li
                key={slug}
                className={cn("rounded-full border border-ink/10 bg-surface-low px-2.5 py-0.5 text-[11px] font-medium text-ink-muted")}
              >
                {getTrade(slug)?.label ?? slug}
              </li>
            ))}
          </ul>
        ) : null}
      </article>
    </li>
  );
}
